/**
 * Native (iOS) subscription screen · StoreKit 2 only.
 * Prices come from the App Store (localized). Premium is granted ONLY after the
 * backend verifies the signed transaction. No Stripe, web-checkout or website
 * purchase direction is ever shown here.
 */
import React, { useEffect, useState } from "react";
import { Check, Loader2, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { storekit } from "@/lib/mobile/storekit";

const INCLUDED = ["Ship Guide: licensed partner content for your ship, where available", "Voyage planning, every workspace, Weekly Briefing and family sharing stay free"];

export default function NativeSubscriptionScreen() {
  const [products, setProducts] = useState(null);
  const [ent, setEnt] = useState(null);
  const [state, setState] = useState("loading");     // loading | ready | unavailable
  const [busy, setBusy] = useState(null);             // productId | "restore" | null
  const [notice, setNotice] = useState(null);

  const loadEntitlement = () => api.get("/api/entitlement/me").then((r) => setEnt(r.data)).catch(() => setEnt(null));

  useEffect(() => {
    loadEntitlement();
    storekit.products().then((r) => { setProducts(r.products || []); setState("ready"); })
      .catch(() => setState("unavailable"));
  }, []);

  const premiumActive = ent?.access_package === "premium";
  const premiumViaApple = premiumActive && ent?.source === "apple";

  const buy = async (productId) => {
    setBusy(productId); setNotice(null);
    try {
      const { data: tok } = await api.post("/api/apple/account-token", {});
      const res = await storekit.purchase(productId, tok.app_account_token);
      if (res.state === "pending") { setNotice("Your purchase is awaiting approval (Ask to Buy). Premium unlocks once it is approved."); return; }
      if (res.state === "cancelled") { setNotice("Purchase cancelled. Nothing was charged."); return; }
      const { data } = await api.post("/api/apple/transactions/verify", { signed_transaction: res.jws });
      if (data.premium_already_active) { setNotice("Premium is already active on your account."); }
      else if (data.accepted) { setNotice("Purchase verified. Premium is active."); }
      await loadEntitlement();
    } catch (err) {
      const status = err?.response?.status; const code = err?.response?.data?.detail?.code || err?.code;
      if (status === 503) setNotice("Subscriptions are not available yet.");
      else if (code === "storekit_unavailable") setNotice("The App Store is unavailable on this device.");
      else setNotice("The purchase could not be completed. You have not been charged unless the App Store shows a receipt.");
    } finally { setBusy(null); }
  };

  const restore = async () => {
    setBusy("restore"); setNotice(null);
    try {
      const r = await storekit.restore();
      const { data } = await api.post("/api/apple/restore", { signed_transactions: r.transactions || [] });
      const ok = (data.results || []).filter((x) => x.accepted).length;
      setNotice(ok ? `Restored ${ok} purchase${ok === 1 ? "" : "s"}.` : "No purchases to restore for this account.");
      await loadEntitlement();
    } catch (_e) { setNotice("Restore could not be completed. Please try again."); }
    finally { setBusy(null); }
  };

  return (
    <main className="min-h-screen bg-[#071e3e] px-5 py-10 text-white" data-testid="cc-native-subscription">
      <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#d4a853]">Voyager membership</p>
      <h1 className="mt-2 text-4xl font-semibold leading-tight">Premium, billed by the App Store.</h1>
      {premiumActive && (
        <p className="mt-4 rounded-xl border border-emerald-300/40 bg-emerald-500/10 p-3 text-sm" data-testid="cc-native-premium-active">
          Premium is already active on your account{premiumViaApple ? "" : ""}.
        </p>
      )}
      <ul className="mt-6 space-y-2 text-[15px] text-white/85">
        {INCLUDED.map((l) => <li key={l} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-[#d4a853]" />{l}</li>)}
      </ul>
      {state === "loading" && <p className="mt-6 flex items-center gap-2 text-sm text-white/60"><Loader2 className="h-4 w-4 animate-spin" /> Loading App Store prices…</p>}
      {state === "unavailable" && <p className="mt-6 text-sm text-white/70" data-testid="cc-native-unavailable">App Store prices are unavailable right now.</p>}
      {state === "ready" && !premiumActive && (
        <div className="mt-6 space-y-3">
          {products.map((p) => (
            <button key={p.id} type="button" disabled={!!busy} onClick={() => buy(p.id)} data-testid={`cc-native-buy-${p.period}`}
              className="flex w-full items-center justify-between rounded-2xl border border-white/20 bg-white/[0.06] px-5 py-4 text-left disabled:opacity-50">
              <span><span className="block text-[15px] font-semibold">{p.displayName}</span>
                <span className="block text-[12px] text-white/60">Renews every {p.period}. Cancel any time in App Store settings.</span></span>
              <span className="text-lg font-semibold" data-testid={`cc-native-price-${p.period}`}>{busy === p.id ? <Loader2 className="h-5 w-5 animate-spin" /> : p.displayPrice}</span>
            </button>
          ))}
        </div>
      )}
      {notice && <p className="mt-4 text-[13px] text-amber-200" data-testid="cc-native-notice">{notice}</p>}
      <div className="mt-8 flex flex-wrap gap-4 text-[13px]">
        <button type="button" onClick={restore} disabled={!!busy} data-testid="cc-native-restore" className="inline-flex items-center gap-1 underline">
          <RefreshCw className="h-3.5 w-3.5" /> Restore Purchases
        </button>
        <button type="button" onClick={() => storekit.manageSubscriptions().catch(() => {})} data-testid="cc-native-manage" className="underline">Manage subscription</button>
      </div>
      <p className="mt-6 text-[12px] leading-snug text-white/55">
        Payment is charged to your Apple ID at confirmation. Subscriptions renew automatically unless cancelled at least 24 hours before the end of the current period, and can be managed in your App Store account settings. No free trial.
        <br /><a href="/legal/terms.html" className="underline">Terms of Service</a> · <a href="/legal/privacy.html" className="underline">Privacy Policy</a>
      </p>
    </main>
  );
}
